import { NewSession } from '../../../state/session/session.actions';
import { UpdateSiteInformation } from '../../../state/configuration/config.actions';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import {
  AcquireImages,
  DestroyScanner,
  FetchApiStatus,
  FormStatusToggle,
  GetScannerList,
  InitializeScanner,
  LoadScanner,
  ResetScans,
  SelectScanner,
} from '../../../state/scanner/scanner.actions';
import { EcaSiteSearchModule, Site, SiteSearchSiteType } from '@drc-eca/eca-components-lib';
import { filter, first, pluck, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { IBatchEvent } from '../../../state/interfaces';
import { PermissionService } from '../../../services/permission.service';
import { AsyncPipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCard } from '@angular/material/card';
import { FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { MatList } from '@angular/material/list';
import { ScanProgressListItemComponent } from '../../scan-progress-list-item/scan-progress-list-item.component';
import { MatButton } from '@angular/material/button';
import { ThrottleClickDirective } from '@drc-eca/eca-lrc-common-ui';
import { AlertBoxComponent } from '../../shared/alert-box/alert-box.component';
import { ScanSessionReviewComponent } from '../../scan-session-review/scan-session-review.component';


@Component({
  selector: 'eca-web-scanner',
  templateUrl: './web-scanner.component.html',
  styleUrls: ['./web-scanner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [MatProgressSpinner, MatCard, EcaSiteSearchModule, FormsModule, MatCheckbox, MatTooltip, MatList, ScanProgressListItemComponent, MatButton, ThrottleClickDirective, AlertBoxComponent, ScanSessionReviewComponent, AsyncPipe]
})
export class WebScannerComponent implements OnInit, OnDestroy {
  private store = inject<Store<any>>(Store);
  private permissionService = inject(PermissionService);


  readonly siteSelectACE = PermissionService.ACE_LOCAL_SCANNING_API_VIEW;
  readonly schoolSiteType = SiteSearchSiteType.School;


  siteInformation$: Observable<Partial<Site>>;
  clientId$: Observable<string>;

  scannerStatus$: Observable<any>;
  scannerInitialized$: Observable<boolean>;
  scannerBusy$: Observable<boolean>;
  currentStatus$: Observable<string>;
  selectedSite$: Observable<string>;
  apiStatusOffline$: Observable<boolean>;
  availableScanners$: Observable<any>;
  activeScanner$: Observable<any>;
  useDraftVersion$: Observable<boolean>;
  showDraftVersionCheckbox$: Observable<boolean>;

  batchStatus$: Observable<IBatchEvent[][]>;

  scannerSelectorShown = false;
  batchToDisplay = 0;
  
  constructor() {

    this.store.dispatch(new LoadScanner(environment.dynamsoftResourcesPath));

    /* model for eca-site-selector*/
    /* istanbul ignore next */
    this.siteInformation$ = this.store.select('siteInformation')
      .pipe(filter(site => site && site.path));
    this.selectedSite$ = this.store.select('siteInformation').pipe(pluck('path'));
    this.clientId$ = this.store.select('siteInformation', 'client');

    this.scannerStatus$ = this.store.select('scannerStatus');
    this.scannerInitialized$ = this.scannerStatus$.pipe(pluck('initialized'));
    this.scannerBusy$ = this.scannerStatus$.pipe(pluck('busy'));
    this.currentStatus$ = this.scannerStatus$.pipe(pluck('current'));
    this.availableScanners$ = this.scannerStatus$.pipe(pluck('availableScanners'));
    this.activeScanner$ = this.scannerStatus$.pipe(pluck('activeScanner'));
    this.apiStatusOffline$ = this.store.select('scannerStatus').pipe(pluck('apiOffline'));
    this.useDraftVersion$ = this.store.select('scans').pipe(
      pluck('formStatus'),
      switchMap((formStatus) => {
        return of(formStatus === 'Draft');
      })
    );

    this.showDraftVersionCheckbox$ = this.permissionService.hasAnyPermission(PermissionService.ACE_TABE_LOCAL_SCANNING_UI_TESTER);

    this.batchStatus$ = this.store.select('batchStatus');
  }

  ngOnInit() {
    this.currentStatus$.pipe(
      filter(status => status !== 'missing' && status !== 'loading'),
      first()
    ).subscribe(() => {
      this.store.dispatch(new InitializeScanner());
    });

    this.store.dispatch(new FetchApiStatus());
  }

  isCurrentBatchCompleted(currentBatch) {
    const batchCompletingActions = ['send-started', 'failed-to-scan', 'nothing-to-scan'];
    const lastStep = currentBatch[currentBatch.length - 1];
    const batchEnded = lastStep ? batchCompletingActions.indexOf(lastStep.status) >= 0 : false;
    return lastStep && batchEnded && lastStep.completed;
  }

  showScannerSelector() {
    this.store.dispatch(new GetScannerList());
    this.toggleScannerSelector();
  }

  toggleScannerSelector() {
    this.scannerSelectorShown = !this.scannerSelectorShown;
  }

  selectScanner(source) {
    this.store.dispatch(new SelectScanner(source));
    this.toggleScannerSelector();
  }

  acquireImages() {
    this.store.dispatch(new NewSession());
    this.store.dispatch(new ResetScans());
    this.store.dispatch(new AcquireImages());
  }

  updateFormStatus() {
    this.store.dispatch(new FormStatusToggle());
  }

  scanAgain() {
    this.acquireImages();
  }

  ngOnDestroy() {
    this.store.dispatch(new DestroyScanner());
  }

  clearSelectedSite() {
    this.store.dispatch(new UpdateSiteInformation({
      path: null,
      parentSiteName: null,
      siteCode: null,
      siteName: null,
    }));
  }

  siteSelectedCallback(site: Partial<Site>) {
    console.log('Selected Site:', site);
    /* istanbul ignore next */
    if (!site) {
      this.clearSelectedSite();
      return;
    }
    this.store.dispatch(new UpdateSiteInformation({
      path: site.path,
      siteName: site.siteName,
      siteCode: site.siteCode,
      parentSiteName: site.parentSiteName,
    }));
  }

}
